<template>
  <div class="" :key="examId">
    <div class="title-box">
      <div class="flex-start">
        <button class="btn-back" type="button" name="button" data-test="btnBack" @click="goBack">
          <IconArrowBack />
        </button>

        <div class="patient-info-actions" v-if="exam">
          <h2 class="exam-patient-name" :class="{'anonymous': isJokerPatient}" @click="openPatientDetails">
            <IconPlusSmall v-if="isJokerPatient"/>
            {{ patientName }}
          </h2>
          <p class="date">
            <IconCalendar/>
            {{ formatDate(exam.date) }}
          </p>
        </div>

        <div class="btn-group ai-status-group" role="group" aria-label="AI Assistant status" v-if="exam && exam.scoreResult">
          <div class="btn btn-outline-secondary ai-assistant-badge" v-if="hasAiAssistantFeature">
            <img src="@/assets/images/ai-assistant/ai-assistant-logo.png" alt="AI Assistant" width="14"/>
            <span>AI ASSISTANT</span>
          </div>

          <div class="btn btn-outline-secondary ai-box" v-if="exam.scoreResult.doubleCircle">
            <p>{{ aiStatusLabel() }}</p>
            <span :class="exam.scoreResult.odStatus ?? ''"></span>
            <span :class="exam.scoreResult.osStatus ?? ''"></span>
          </div>

          <div class="btn btn-outline-secondary ai-box" v-if="!exam.scoreResult.doubleCircle">
            <p>{{ aiStatusLabel() }}</p>
            <span :class="exam.scoreResult.odStatus ?? ''"></span>
          </div>
        </div>

        <button class="btn btn-outline-secondary" v-if="!isSpecialist && !isJokerPatient" type="button" name="button"
          :data-title="$t('externalPatient.shareSimple')" data-test="btnPatientShare" @click="shareInformation">
          <IconShare />
          <span>{{ $t('externalPatient.shareSimple') }}</span>
        </button>

        <div class="btn-group" role="group" :aria-label="$t('exam.examActionsGroupLabel')">
          <button class="btn btn-outline-secondary" v-if="canRequestConsent" type="button" name="button"
            :data-title="$t('examConsent.title')" data-test="`btnExamConsent`" @click="openConsentOffcanvas">
            <IconConsent/>
          </button>

          <button class="btn btn-outline-secondary" v-if="canDownloadConsent" type="button" name="button"
            :data-title="$t('examConsent.downloadSigned')" data-test="`btnExamHasImage`" @click="doDownloadConsent(consentUser, false)">
            <IconConsent/>
          </button>

          <button class="btn btn-outline-secondary no-border-radius" v-if="!isSpecialist && !isJokerPatient" type="button" name="button"
            :data-title="$t('tooltips.download')" data-test="`btnExamHasImage`" @click="doDownload">
            <IconDownload />
          </button>

          <button class="btn btn-outline-secondary no-border-radius" v-if="exam && hasClaimFeature" type="button" name="button"
            :data-title="$t('examClaim.previewTooltip')" data-test="btnExamClaimPreview" @click="openClaimPreviewOffcanvas">
            <i class="fa fa-clipboard fa-2x" aria-hidden="true"></i>
          </button>

          <button class="btn btn-outline-secondary" v-if="isExamDataOthersUploadAllowed" type="button" name="button"
            :data-title="$t('tooltips.upload')" data-test="`btnExamHasImage`" @click="openUploadOffcanvas">
            <IconPlusSmall/>
          </button>
        </div>
      </div>

      <button class="btn btn-outline-secondary" v-if="isPowerfull" type="button" name="button"
        :data-title="$t('remove')" data-test="btnPatientRemove" @click="confirmRemoval">
        <IconTrash />
      </button>
    </div>

    <BaseTab class="exam-show-tab" v-if="patient" :tabs="tabs" :initialtab="tabActive" :key="patient.id" @tab-activated="switchTab">
      <template v-slot:tab-head-images>
        {{ $t("exam.images") }}
      </template>
      <template v-slot:images>
        <ExamImageTab v-if="tabActive === 'images' && exam" :exam_id="exam.id" :patient="patient"
          :alert_msg="alert ? alert : null"/>
      </template>

      <template v-slot:tab-head-others>
        {{ $t("exam.others") }}
      </template>
      <template v-slot:others>
        <ExamOthersTab v-if="isExamOthersTabAllowed"
          :exam_id="exam.id" :patient="patient" :key="exam.updatedAt"/>
      </template>

      <template v-slot:tab-head-aiAssistant>
        <span class="tab-head-nowrap">
          <img src="@/assets/images/ai-assistant/ai-assistant-logo.png" alt="AI Assistant logo" width="14"/>
          {{ $t("exam.aiAssistant") }}
        </span>
      </template>
      <template v-slot:aiAssistant>
        <ExamAiAssistantTab v-if="tabActive === 'aiAssistant' && exam" :exam_id="exam.id" :patient="patient"/>
      </template>

      <template v-slot:tab-head-data>
        {{ $t("exam.information") }}
      </template>
      <template v-slot:data>
        <ExamInfoTab v-if="tabActive === 'data' && exam" :exam="exam" :patient="patient"
          :alert_msg="alert ? alert : null"/>
      </template>

      <template v-slot:tab-head-report>
        {{ $t("exam.report") }}
      </template>
      <template v-slot:report>
        <ExamReportTab v-if="tabActive === 'report' && exam"
                       :patient="patient"
                       :exam_id="exam.id"
                       :patient-report-info="patientReportInfo"
                       :unified-exam-type="exam.unifiedExam?.type ?? null"/>
      </template>

      <template v-slot:tab-head-partners>
        {{ $t('partnerResults.tab') }}
      </template>
      <template v-slot:partners>
        <ExamPartnerResultsTab v-if="tabActive === 'partners' && exam"
                               :exam_uuid="exam.uuid"
                               :clinic_uuid="session.currentClinic.uuid"/>
      </template>
    </BaseTab>

    <teleport to="body">
      <BaseGenericOffcanvas :offcanvas_id="'offcanvasExamDeleteGeneric'" ref="offcanvasGeneric" />
      <BasePatientAccessInfoOffcanvas ref="offcanvasPatientAccessInfo" />
      <ExamAssignPatientOffcanvas :offcanvas_id="'ExamAssignPatientOffcanvas'" ref="examAssignPatientOffcanvas" @patient-assigned="closeAssignOffCanvas"/>
      <ExamConsentOffcanvas ref="examConsentOffcanvas" />
      <ExamUploadOffcanvas ref="examUploadOffcanvas" />
      <EdiClaimPreviewOffcanvas :offcanvas_id="'EdiClaimPreviewOffcanvas'" ref="ediClaimPreviewOffcanvas"/>
    </teleport>
  </div>
</template>

<style lang="scss" scoped>
.breadcrumb-item,
.title-box h2 {
  text-transform:                  capitalize;
}

.tab-head-nowrap {
  display:                         inline-flex;
  align-items:                     center;
  gap:                             6px;
  white-space:                     nowrap;
}

:deep(#aiAssistant.nav-link) {
  padding:                         13px 15px;
}

.title-box {
  .flex-start {
    gap:                           10px;

    > div:not(.btn-group) {
      display:                     flex;
      flex-direction:              row;
      justify-content:             flex-start;
      align-items:                 center;
      gap:                         10px;

      &.patient-info-actions {
        gap:                       0;

        h2,
        .date {
          color:                   var(--Brand-Primary);
          line-height:             normal;
          font-weight:             500;
          text-overflow:           ellipsis;
          overflow:                hidden;
          display:                 flex;
          align-items:             center;
          gap:                     5px;
          height:                  37px;
          margin:                  0;
          padding:                 10px 15px;
          border:                  1px solid var(--Shades-30);
          background:              var(--Shades-W);
        }

        .date {
          @include                 border_radius(0 99px 99px 0);
          font-size:               var(--font-size-12);
        }

        h2 {
          font-size:               var(--font-size-15);

          &.exam-patient-name {
            @include               border_radius(99px 0 0 99px);
            font-weight:           600;
            text-overflow:         ellipsis;
            text-transform:        capitalize;
            line-height:           normal;
            overflow:              hidden;
            padding:               1px 10px;
            border:                1px solid var(--Shades-30);
            cursor:                pointer;

            &.anonymous {
              color:               var(--Shades-50) !important;
              font-weight:         400;
              background:          var(--Shades-10);
              border-style:        dashed;

              svg * {
                stroke:            var(--Shades-50) !important;
              }
            }
          }
        }
      }
    }
  }

  .btn-outline-secondary {
    font-size:                     var(--font-size-12);
    height:                        37px; // 40px
    margin:                        0;
    padding:                       10px;
    background:                    var(--Shades-W);
  }

  .btn-back {
    @include                       border_radius(50%);
    width:                         37px;
    height:                        37px;
    margin:                        0;
    padding:                       13px;
  }

  .flex-start > div:last-child .btn-outline-secondary:last-child {
    padding:                       11.5px 15px;

    svg {
      margin:                      0;

      * {
        stroke:                    var(--Brand-Primary);
      }
    }
  }

  .btn-group {
    .btn-outline-secondary:not(.dropdown-toggle) {
      @include                      border_radius(99px 0 0 99px);
      color:                        var(--Shades-70);
      font-size:                    12px;
      font-weight:                  400;
      line-height:                  normal;
      letter-spacing:               1.8px;
      text-transform:               uppercase;
      display:                      flex;
      justify-content:              flex-end;
      align-items:                  center;
      gap:                          10px;
      height:                       37px;
      margin:                       0;
      padding:                      10px 15px;
      background:                   var(--Shades-W);
      border:                       1px solid var(--Shades-30-Border);

      &.no-border-radius {
        @include                    border_radius(0);
      }

      &:first-child {
        @include                    border_radius(99px 0 0 99px);
        padding:                    10px 10px 10px 15px;
      }

      &:last-child {
        @include                    border_radius(0 99px 99px 0);
        padding:                    10px 12px 10px 10px !important;
      }

      &:only-child {
        @include                    border_radius(99px);
        padding:                    10px 12px !important;
      }

      &.no-border-radius:not(:first-child),
      &:last-child:not(:only-child) {
        border-left:                1px solid transparent;
      }

      &:focus,
      &:hover,
      &:active,
      &.active {
        border:                     1px solid var(--bs-btn-hover-border-color);
      }
    }
  }

  .ai-status-group {
    align-self:                    center;
    display:                       flex;

    &.btn-group .ai-box {
      gap:                          unset;
    }

    .ai-assistant-badge {
      border-radius:                99px 0 0 99px !important;
    }

    .ai-box {
      margin-left:                  -1px !important;
      border-radius:                0 99px 99px 0 !important;
    }

    &.btn-group .ai-box:only-child,
    &.btn-group .btn:only-child {
      margin-left:                  0 !important;
      @include                      border_radius(99px);
    }

    .ai-assistant-badge,
    .ai-box {
      box-sizing:                   border-box;
      height:                       40px;
      padding:                      0 10px;
      line-height:                  38px;
      background:                   var(--Shades-BG);
    }

    .ai-assistant-badge,
    .ai-box {
      cursor:                       default;

      &:hover,
      &:focus,
      &:active {
        background:                 var(--Shades-BG);
        border-color:                var(--Shades-30-Border);
      }
    }
  }

  .ai-assistant-badge {
    display:                       inline-flex;
    align-items:                   center;
    white-space:                   nowrap;
    gap:                           6px;

    img {
      display:                     block;
      flex-shrink:                 0;
    }

    span {
      color:                       var(--Shades-70);
      font-size:                   var(--font-size-10);
      font-weight:                 700;
      letter-spacing:              1.5px;
    }
  }
}

.icon-upload {
  @include                         rotate(180deg);
}

.consent-icon,
.fa-clipboard {
  color:                           var(--Brand-Primary) !important;
}

.consent-icon {
  font-size:                       9px;
  margin-right:                    5px;
  color:                           var(--Brand-Primary) !important;
}

.fa-clipboard {
  font-size:                       18px;
}

.exam-show-tab {
  :deep(.inside-box) {
    // max-height:                    calc(100vh - 182px); // replaced by flex: 1 in _scaffold.scss
  }
}

@media (max-width: 991px) {
  .sub-header-box,
  .title-box .btn-outline-secondary span:not(.consent-icon) {
    display:                       none;
  }

  .title-box {
    display:                       flex !important;
    gap:                           20px;
    padding:                       10px;
    overflow-x:                    auto;

    h2 {
      font-size:                   var(--font-size-18);
      white-space:                 nowrap;
    }

    .flex-start {
      > div.btn-group {
        gap:                       0;
      }
    }

    .btn-outline-secondary {
      align-items: center;
      padding:                     11.5px 15px;

      svg {
        margin:                    0;
      }

      span.consent-icon i {
        padding-left:              1px;
      }
    }

    .btn-back,
    .btn-outline-secondary {
      height:                      35px;
      margin:                      0 !important;
    }

    .ai-status-group {
      .ai-assistant-badge,
      .ai-box {
        height:                    35px;
        line-height:               33px;
        flex-shrink:               0;

        p,
        span {
          display:                 inline;
          white-space:             nowrap;
        }
      }
    }
  }
}

@media screen and (min-width: 992px) and (max-width: 1365px) {
  .retract .exam-show-tab,
  .exam-show-tab {
    :deep(.inside-box) {
      // max-height:                  calc(100dvh - 164px); // replaced by flex: 1 in _scaffold.scss
      // max-height:                  calc(100dvh - 182px);
    }
  }
}
</style>

<script>
import { useUserStore } from '@/store/user'
import ExamAssignPatientOffcanvas from '../ExamAssignPatientOffcanvas.vue'
import ExamImageTab from './ExamImageTab.vue'
import ExamOthersTab from './ExamOthersTab.vue'
import ExamAiAssistantTab from './ExamAiAssistantTab.vue'
import ExamInfoTab from './ExamInfoTab.vue'
import ExamReportTab from './ExamReportTab.vue'
import ExamPartnerResultsTab from './ExamPartnerResultsTab.vue'
import ExamConsentOffcanvas from './ExamConsentOffcanvas.vue'
import ExamUploadOffcanvas from '../ExamUploadOffcanvas.vue'
import EdiClaimPreviewOffcanvas from '../../edi/EdiClaimPreviewOffcanvas.vue'

export default {
  name: 'ExamShow',
  components: {
    ExamAssignPatientOffcanvas,
    ExamImageTab,
    ExamOthersTab,
    ExamAiAssistantTab,
    ExamInfoTab,
    ExamPartnerResultsTab,
    ExamReportTab,
    ExamConsentOffcanvas,
    ExamUploadOffcanvas,
    EdiClaimPreviewOffcanvas
  },
  data: () => ({
    alert: {
      message: null,
      class: null
    },
    msgError: null,
    user: null,
    tabActive: 'images',
    // tabs: ['images', 'data', 'report'],
    deleteID: null,
    exam: null,
    patient: null,
    previous: null,
    isSpecialist: null,
    patientName: null,
    isJokerPatient: false,
    patientReportInfo: {},
    hasOthersExamData: null,
    consentUser: null,
    consentChecked: false,
    isPowerfull: null,
    hasClaimFeature: false,
    hasConsentFlag: false,
  }),
  computed: {
    examId() {
      return this.$route.params.examID;
    },
    tab() {
      return this.$route.query.tab;
    },
    hasAiAssistantFeature() {
      return this.$utils.canUseAiAssistant(this.session)
    },
    tabs() {
      const tabs = ['images']

      if (this.hasAiAssistantFeature)
        tabs.push('aiAssistant')

      if (this.isJokerPatient)
        return tabs

      tabs.push('data', 'report')

      if (this.hasOthersExamData && !this.isSpecialist)
        tabs.push('others')

      return tabs
    },
    isExamDataOthersUploadAllowed() {
      return this.hasOthersExamData && !this.isSpecialist
        && !this.isJokerPatient;
    },
    isExamOthersTabAllowed() {
      return this.tabActive === 'others' && this.exam
        && !this.isSpecialist;
    },
    canRequestConsent() {
      return this.hasConsentFlag && this.consentChecked && !this.isSpecialist &&
        !this.isJokerPatient && !this.consentUser;
    },
    canDownloadConsent() {
      return this.hasConsentFlag && this.consentChecked && !this.isSpecialist && this.consentUser;
    }
  },
  created() {
    // watch param exam id coming from notifications to force reload data
    this.$watch( // TODO REFACTOR KISS
      () => this.$route.params.examID,
      (toParams, previousParams) => {
        if (toParams != previousParams && this.$route.name == 'exam-show')
          this.fetchData()
      }
    )
  },
  mounted () {
    this.$activeLink.value = 'exam'

    const userStore = useUserStore()
    this.session = userStore.sessionInfo

    this.isSpecialist = this.session.isSpecialist
    this.isPowerfull = this.session.isPowerfull;
    this.hasOthersExamData = this.session.features.includes('othersExamData')
    this.hasClaimFeature = this.session.features.includes('claims')
    this.hasConsentFlag = this.session.features.includes('consentFlag');

    this.fetchData()
    // this.fetchReportData()

    if (this.tab) {
      this.switchTab(this.tab)
    }

    window.addEventListener('popstate', function() {
      document.querySelector('.offcanvas-backdrop')?.remove()
    })

    document.body.classList.remove('has-filter')

    this.emitter.on('open-assign-patient-offcanvas', this.handleOpenAssignPatientOffcanvas)
  },
  methods: {
    goBack() {
      if (this.$route.query.data) {
        let data = this.$utils.parseDataQuery(this.$route.query.data)
        this.previous = data.previous
      }

      if (this.previous == 'patient') {
        let data = this.$utils.dataQuery({
          patientName: this.patient.rawName
        })

        this.$router.push({ name: 'patient-show', params: { patientID: this.patient.id }, query: { tab: 'exams', data } })
      } else {
        let data = this.$route.query.data
        this.$router.push({ name: 'exam-list', query: { data } })
      }
    },
    switchTab(tab) {
      this.tabActive = tab;
      // this.$router.push({name: 'exam-show', params: {examID: this.examId, tab: tab}})
    },
    // Same rule as ExamGridList.vue's own aiStatusLabel — the AI-assigned finding when
    // there is one, "Não alterado" when multiclass genuinely found nothing,
    // "Foto sem qualidade avaliativa" when there's no usable reading for either eye at
    // all (see hasUnevaluableQuality below), or the plain "AI" fallback for any other case
    // multiclass never ran for at all (alterationText itself undefined, not just empty —
    // see insertScoreResultInto/hasAiAssistantFeature above).
    // The quality-specific message is gated on hasAiAssistantFeature same as alterationText
    // itself — a clinic without the flag keeps the exact same plain "AI" fallback it
    // always had, regardless of what the dots look like.
    aiStatusLabel() {
      const alterationText = this.exam?.scoreResult?.alterationText
      if (alterationText !== undefined) {
        if (!alterationText.length) return this.$t('exam.notAltered')
        return this.$t(alterationText[0])
      }
      if (this.hasAiAssistantFeature && this.hasUnevaluableQuality()) return this.$t('exam.unevaluableQuality')
      return 'AI'
    },
    // Whether every dot this exam has (both eyes, or just the one for the older
    // single-circle shape — see scoreResult.doubleCircle) landed on 'no-info' — see
    // ExamGridList.vue's own copy of this method for the full reasoning.
    hasUnevaluableQuality() {
      const scoreResult = this.exam?.scoreResult
      const statuses = scoreResult?.doubleCircle ? [scoreResult?.odStatus, scoreResult?.osStatus] : [scoreResult?.odStatus]
      return statuses.every(status => status === 'no-info')
    },
    async fetchData () {
      NProgress.start()
      this.consentUser = null
      this.consentChecked = false
      try {
        let params = {
          id: this.examId,
          researchFlag: this.session.features.includes('researchFlag')
        }

        let response = await this.$examService.get(params);
        this.exam = response.exam

        // Same call ExamGridList.vue/ExamTableList.vue make (via exam-list.js) to get
        // exam.scoreResult — both the AI Assistant badge and the status (.ai-box) badge
        // above gate on that, not on the tab's own captures (this.exam here is a plain
        // list-shaped exam object with .metadata/.hasImage, not the AI Assistant tab's own
        // per-capture aiMetadata). alterationText (the finding text inside the .ai-box
        // pill) follows hasAiAssistantFeature alone, same as ExamGridList.vue's own badge —
        // this page has no mutirao-only white alteration-list to defer to, so there's no
        // allAlterationText to compute here either.
        this.$aiService.insertScoreResultInto(this.exam, this.hasAiAssistantFeature, false)

        this.patient = response.patient

        this.isJokerPatient = this.patient.uuid == this.session.currentClinic.jokerPatientUuid
        this.patientName = this.isJokerPatient ? this.$i18n.t('noPatientSelected') : this.patient.fullName
        this.tabs = this.isJokerPatient ? ['images'] : this.tabs

        const dateFormat = this.$i18n.t('dateFormat')
        this.patientReportInfo = {
          dateOfScreening: this.exam.date ? moment.utc(this.exam.date).format(dateFormat) : '-',
          dateOfBirth: this.patient.birthday ? moment.utc(this.patient.birthday).format(dateFormat) : '-',
          patientFullName: this.patient.fullName
        }

         await this.loadConsentPdf()
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
        // this.alert = this.$message.alert(this, 'request_error', 'danger')
      } finally {
        NProgress.done(true)
      }
    },
    toPatient() {
      let data = this.$utils.dataQuery({
        patientName: this.patient.rawName
      })

      if (!this.isJokerPatient) {
        this.$router.push({ name: 'patient-show', params: { patientID: this.patient.id }, query: { tab: 'exams', data } })
      }
    },
    activeAiClasses(exam) {
      return this.$aiService.mcRASClasses(exam)
    },
    formatDate(date) {
      return moment(date).format(this.$i18n.t('dateFormat'))
    },
    async shareInformation() {
      const ok = await this.$refs.offcanvasPatientAccessInfo.show({
        patient: this.patient
      })
      if (ok) {
        NProgress.done(true)
      }
    },
    async confirmRemoval() {
      const ok = await this.$refs.offcanvasGeneric.show({
        title: this.$i18n.t('confirm'),
        text: this.$i18n.t('exam.confirmExamRemoval'),
        okButton: this.$i18n.t('remove'),
        okButtonClass: 'btn-danger',
        // cancelButton: this.$i18n.t('cancel'),
        // cancelButtonClass: 'btn-outline-secondary'
      })
      if (ok) {
        this.doDelete()
      }
    },
    doDownload(){
      try {
          NProgress.start()
          this.$examService.download(this.exam.id)
      } catch (err) {
          this.$message.popup(this, 'request_error', 'operationFail', 'error')
      }
      NProgress.done(true)
    },
    async doDelete () {
      this.alert.message = null;

      let params = {
        id: this.exam.id,
      }

      NProgress.start()
      try {
        await this.$examService.remove(params)
        this.$message.popup(this, 'general.removedSuccess', 'operationSuccess', 'success')

        // TODO ENH change behavior:
        // when comming from patient > exams, go back to patient/exams/list
        // when comming from home > exams, go back to exams
        // this.$router.push({name: 'exam-list'})
        this.goBack()
      } catch (err) {
        this.$message.popup(this, 'general.removedError', 'operationFail', 'error')
        // this.alert = this.$message.alert(this, 'general.removedError', 'danger')
      }
      NProgress.done(true)
    },
    async openAssignOffcanvas() {
      if (this.isSpecialist)
        return

      await this.$refs.examAssignPatientOffcanvas.show({
        exam: this.exam,
        reassign: !this.isJokerPatient,
        patientFullName: this.patientName
      })
    },
    handleOpenAssignPatientOffcanvas(payload) {
      if (this.isSpecialist)
        return

      this.$refs.examAssignPatientOffcanvas.show(payload)
    },
    openPatientDetails() {
      if (this.isJokerPatient) {
        this.openAssignOffcanvas()
        return
      }

      this.emitter.emit('open-patient-details', { patientId: this.patient.id, exam: this.exam })
    },
    closeAssignOffCanvas(updatedExam) {
      this.$nextTick(() => {
        this.fetchData()
      })

      if (updatedExam) {
        this.emitter.emit('patient-reassigned', updatedExam)
      }
    },
    async openConsentOffcanvas() {
      const saved = await this.$refs.examConsentOffcanvas.show({
        examId: this.exam.id,
        patientId: this.patient.id
      });

      if (saved) {
        this.fetchData();
      }
    },
    async loadConsentPdf() {
      try {
        const response = await this.$examDataService.list({ id: this.examId });
        const list = response.examDataList || [];

        const consentFiles = list.filter(ed => ed.type === 'OTHER_CONSENT_PDF' && !ed.deleted);
        if (consentFiles.length > 0) {
          this.consentUser = consentFiles[consentFiles.length - 1];
        } else {
          this.consentUser = null;
        }
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error');
      } finally {
        this.consentChecked = true;
      }
    },
    async doDownloadConsent(item) {
      NProgress.start();

      try {
        const canDownload = await this.$examDataService.canDownloadFile({ uuid: item.uuid });
        if (!canDownload)
          return;

        const url = this.$examDataService.getDownloadFileUrl({ uuid: item.uuid });
        this.$utils.triggerFileDownload(url)
        } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error');
      } finally {
        NProgress.done(true);
      }
    },
    async openUploadOffcanvas() {
      const saved = await this.$refs.examUploadOffcanvas.show({
        exam: {
          ...this.exam,
          patient: this.patient
        }
      });

      if (saved) {
        this.fetchData();
      }
    },
    async openClaimPreviewOffcanvas() {
      const ok = await this.$refs.ediClaimPreviewOffcanvas.show({
        examID: this.examId
      })
      if (ok) {
        // this.fetchData()
      }
    }
  },
  unmounted() {
    this.tabActive = 'images'
    this.emitter.off('open-assign-patient-offcanvas', this.handleOpenAssignPatientOffcanvas)
  },
}
</script>
